/**
 * Fetch the report PDF exactly as the server renders it for download, email and HL7,
 * so viewing a report shows the same document everyone else receives.
 *
 * A plain function, not a thunk: the blob is shown and thrown away, it is not state.
 */

import { getToken } from './authUtils';
import { REPORTS } from '../constant';

export type FetchReportPdfResult = { ok: true; blob: Blob } | { ok: false; error: string };

export async function fetchReportPdf(
  reportId: string,
  reportDiagramHtml = ''
): Promise<FetchReportPdfResult> {
  if (!reportId) return { ok: false, error: 'No report selected.' };

  let response: Response;
  try {
    response = await fetch(REPORTS.DOWNLOAD_PDF, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + getToken(),
      },
      body: JSON.stringify({ _id: reportId, reportDiagramHtml }),
    });
  } catch {
    return { ok: false, error: 'Could not reach the server. Check your connection and try again.' };
  }

  if (!response.ok) {
    let message = `The server refused the request (${response.status}).`;
    try {
      const parsed = JSON.parse(await response.text());
      if (parsed?.msg) message = String(parsed.msg);
    } catch {
      /* not JSON — keep the status-based message */
    }
    return { ok: false, error: message };
  }

  const blob = await response.blob();
  if (blob.type !== 'application/pdf' || blob.size === 0) {
    return { ok: false, error: 'The server did not return a PDF.' };
  }
  return { ok: true, blob };
}

/** Phones and tablets: their browsers often can't show a PDF inside the page. */
export function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}
